import { useMemo } from 'react';
import { MultiRosterProvider } from '../context/MultiRosterContext';
import { useSharedPlayerData } from '../hooks/useSharedPlayerData';
import { createSharedLeagueConfig } from '../utils/sharedLeagueConfig';
import { PageLoader } from '../components/LoadingSpinner';
import TradeCalculator from './TradeCalculator';
import type { LeagueFormatType } from '../components/LeagueFormatSelector';
import { useClerkSession } from '../hooks/useClerkSession';

/**
 * Wrapper component that provides MultiRosterProvider to Trade Calculator
 * This enables roster-aware features and personalized alternatives
 */
const TradeCalculatorWithRoster = () => {
  const { isLoaded, signedIn } = useClerkSession();

  if (!isLoaded) {
    return <PageLoader pageName="Trade Calculator" loadingText="Loading..." />;
  }

  if (!signedIn) {
    return (
      <MultiRosterProvider playersPool={[]}>
        <TradeCalculator />
      </MultiRosterProvider>
    );
  }

  return <SignedInTradeCalculator />;
};

const SignedInTradeCalculator = () => {
  // Use standard format for initial data loading - user can change format in the UI
  const leagueConfig = useMemo(() => {
    return createSharedLeagueConfig({
      format: 'standard' as LeagueFormatType,
      scoringFormat: 'standard',
      teamCount: 12,
      isSuperflex: false
    });
  }, []);

  // Load players for the MultiRosterProvider
  const { players: playersPool, isLoading: playersLoading } = useSharedPlayerData({
    leagueConfig,
    scaleToTen: true
  });

  if (playersLoading) {
    return (
      <PageLoader 
        pageName="Trade Calculator"
        loadingText="Loading..."
      />
    );
  }

  return (
    <MultiRosterProvider playersPool={playersPool}>
      <TradeCalculator />
    </MultiRosterProvider>
  );
};

export default TradeCalculatorWithRoster;
